/**
 * Build the entity-shaped optimistic patch for an `updateProject` call.
 *
 * `UpdateProjectPayload` is NOT entity-shaped — a naive `{ ...project,
 * ...payload }` would corrupt the collection: `payload.fields` is a single-
 * element array that would REPLACE the whole `fields[]`, and `payload.customer`
 * is a `{ companyId }` stub, not a full `Company`. So we translate the payload
 * into a `DeepPartial<Project>` the collection can shallow-merge safely:
 * - custom-field edits merge into the existing `fields[]` by `fieldId`
 * - native edits map to their top-level key as-is
 *
 * Mirrors the legacy `patchProjectByPayload` (`queries/project.tsx`) so the
 * optimistic value matches what the old cache-as-store produced.
 */ export const buildProjectOptimisticPatch = (original, payload)=>{
    var _original_fields;
    if (!payload.fields) {
        // Native field edit — pass the changed top-level keys straight through.
        // (`customer`/`partners` stubs are not patched optimistically; the server
        // response re-hydrates the full relation on commit.)
        const { fields: _fields, ...native } = payload;
        return native;
    }
    const [payloadField] = payload.fields;
    const existing = (_original_fields = original.fields) !== null && _original_fields !== void 0 ? _original_fields : [];
    const hasField = existing.some((f)=>f.fieldId === payloadField.fieldId);
    const fields = hasField ? existing.map((f)=>{
        if (f.fieldId !== payloadField.fieldId) {
            return f;
        }
        const merged = {
            ...f,
            fieldValue: payloadField.fieldValue
        };
        // Readers (e.g. the status highlight card) derive the displayed value
        // from `metaFieldValue.value` (matched against `metaData.options`), NOT
        // `fieldValue`. For select-type fields `metaFieldValue.value` equals the
        // chosen `fieldValue`, so mirror it here — otherwise the optimistic edit
        // updates `fieldValue` but the card keeps showing the old option until
        // the server round-trip rehydrates `metaFieldValue`. Only touch fields
        // that already carry a `metaFieldValue` (the server re-hydrates the exact
        // shape on commit; this is just the optimistic display).
        // `metaFieldValue.value` is numeric (a select option value); only mirror
        // when the new value is a number (status/select edits) — other field
        // types keep their server-rehydrated metaFieldValue on commit.
        if (f.metaFieldValue != null && typeof payloadField.fieldValue === 'number') {
            merged.metaFieldValue = {
                ...f.metaFieldValue,
                value: payloadField.fieldValue
            };
        }
        return merged;
    }) : [
        ...existing,
        payloadField
    ];
    return {
        fields
    };
};
